import { getJwtPermissions, getJwtRoles } from './JwtDecoder';
import { RoleList, isAdminRole } from './RoleManager';
import { SpotSyncAuth } from '../Data/Atoms';

export type Permission = string;

export interface MonitorPermissionMeta {
    key: Permission;
    displayName: string;
    description: string;
    category: string;
    sortOrder: number;
}

export const MONITOR_PERMISSIONS: MonitorPermissionMeta[] = [
    { key: 'edit-listing', displayName: 'Edit Listing', description: 'Edit facility listing details, address, and capacity', category: 'Facilities', sortOrder: 5 },
    { key: 'manage-integrations', displayName: 'Manage Integrations', description: 'Enroll and manage third-party integrations', category: 'Integrations', sortOrder: 20 },
    { key: 'view-subscription-audit-log', displayName: 'View Subscription Audit Log', description: 'View subscription change audit history', category: 'Parkers', sortOrder: 30 },
    { key: 'manage-monthly', displayName: 'Manage Monthly', description: 'Invite parkers, edit monthly subscriptions, and import parkers', category: 'Parkers', sortOrder: 32 },
    { key: 'view-parker-notes', displayName: 'View Parker Notes', description: 'View notes on a parker profile', category: 'Parkers', sortOrder: 33 },
    { key: 'add-parker-notes', displayName: 'Add Parker Notes', description: 'Add, edit, and delete own notes on a parker profile', category: 'Parkers', sortOrder: 34 },
    { key: 'can-reprint-invoice', displayName: 'Reprint Invoice', description: 'Reprint or resend invoices', category: 'Finance', sortOrder: 40 },
    { key: 'add-credit', displayName: 'Add Credit', description: 'Add account credit to parker balances', category: 'Finance', sortOrder: 45 },
    { key: 'collect-payment', displayName: 'Collect Payment', description: 'Record check and imported payments', category: 'Finance', sortOrder: 46 },
    { key: 'refund-payments', displayName: 'Refund Payments', description: 'View Stripe payment details and issue refunds', category: 'Finance', sortOrder: 47 },
    { key: 'adjust-orders-payments', displayName: 'Adjust Orders and Payments', description: 'Adjust order amounts on invoices and adjust non-Stripe payments', category: 'Finance', sortOrder: 48 },
    { key: 'manage-finance', displayName: 'Manage Finance', description: 'Access the facility Finance section', category: 'Finance', sortOrder: 50 },
    { key: 'run-reports', displayName: 'Run Reports', description: 'View and run facility and operator reports', category: 'Reports', sortOrder: 60 },
    { key: 'edit-plan', displayName: 'Edit Plans', description: 'Create and edit monthly plans and prices', category: 'Plans', sortOrder: 70 },
    { key: 'edit-rates', displayName: 'Edit Rates', description: 'Create and edit hourly, event, and special rate programs', category: 'Rates', sortOrder: 80 },
    { key: 'edit-validations', displayName: 'Edit Validations', description: 'Create and edit validation rate programs', category: 'Rates', sortOrder: 85 },
];

export interface Claims {
    roles: string[];
    customClaims?: Record<string, any>;
}

export const ROUTE_PERMISSIONS: Record<string, string[]> = {
    '/operator': ['OperatorAdmin', 'SuperAdmin'],
    '/operator/facilities': ['FacilityUser'],
    '/operator/reports': ['FacilityUser'],
    '/operator/facility/create': ['OperatorAdmin', 'SuperAdmin'],
    '/admin': ['SuperAdmin'],
    '/facility/:id/overview': ['FacilityUser'],
    '/facility/:id/listing': ['FacilityUser'],
    '/facility/:id/parker': ['FacilityUser'],
    '/facility/:id/plan': ['FacilityUser'],
    '/facility/:id/rate': ['FacilityUser'],
    '/facility/:id/finance': ['FacilityUser'],
    '/facility/:id/reports': ['FacilityUser'],
    '/facility/:id/parker/:parkerId': ['FacilityUser'],
};

/** Facility routes that require a JWT permission in addition to role access. */
export const ROUTE_ACTION_PERMISSIONS: Record<string, Permission> = {
    '/facility/:id/listing': 'edit-listing',
    '/facility/:id/finance': 'manage-finance',
    '/facility/:id/reports': 'run-reports',
    '/operator/reports': 'run-reports',
};

export const ROLE_FACILITY_LANDING: Record<string, string> = {
    [RoleList.FacilityUser.identity]: 'overview',
    [RoleList.TransientReviewer.identity]: 'status',
};

function resolvePermissions(auth?: SpotSyncAuth): string[] {
    if (auth?.claims?.permissions && Array.isArray(auth.claims.permissions)) {
        return auth.claims.permissions;
    }
    if (auth?.accessToken) {
        return getJwtPermissions(auth.accessToken);
    }
    return [];
}

export function extractClaims(auth?: SpotSyncAuth): Claims {
    if (!auth || !auth.account) {
        return { roles: [], customClaims: {} };
    }

    const roles = auth.account.roles || auth.roles || getJwtRoles(auth.accessToken ?? '') || [];
    const permissions = resolvePermissions(auth);

    return {
        roles,
        customClaims: {
            ...(auth.claims || {}),
            permissions,
        },
    };
}

function getRoleIdentities(roles: string[]): string[] {
    return roles.map(roleName => {
        const roleKey = Object.keys(RoleList).find(
            key => RoleList[key as keyof typeof RoleList].name === roleName
        );
        return roleKey ? RoleList[roleKey as keyof typeof RoleList].identity : roleName.toUpperCase();
    });
}

function matchPathPattern(pathname: string, pattern: string): boolean {
    const regexPattern = pattern
        .replace(/:[^\/]+/g, '\\d+')
        .replace(/\//g, '\\/');

    const regex = new RegExp(`^${regexPattern}(\/.*)?$`);
    return regex.test(pathname);
}

function findMatchingRoute(pathname: string): string | null {
    const sortedRoutes = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length);
    for (const route of sortedRoutes) {
        if (matchPathPattern(pathname, route)) {
            return route;
        }
    }

    return null;
}

function findActionPermissionForPath(pathname: string): Permission | null {
    const sortedRoutes = Object.keys(ROUTE_ACTION_PERMISSIONS).sort((a, b) => b.length - a.length);
    for (const route of sortedRoutes) {
        if (matchPathPattern(pathname, route)) {
            return ROUTE_ACTION_PERMISSIONS[route];
        }
    }
    return null;
}

export function canAccessPath(claims: Claims, pathname: string): boolean {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return false;
    }

    if (claims.roles.includes(RoleList.SuperAdmin.name)) {
        return true;
    }

    if (claims.roles.includes(RoleList.OperatorAdmin.name) && !pathname.startsWith('/admin')) {
        return true;
    }

    const matchingRoute = findMatchingRoute(pathname);

    if (!matchingRoute) {
        return true;
    }

    const allowedRoles = ROUTE_PERMISSIONS[matchingRoute];
    if (!claims.roles.some(role => allowedRoles.includes(role))) {
        return false;
    }

    const requiredAction = findActionPermissionForPath(pathname);
    if (requiredAction && !canPerformAction(claims, requiredAction)) {
        return false;
    }

    return true;
}

export function canPerformAction(claims: Claims, action: Permission): boolean {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return false;
    }

    if (isAdminRole(claims.roles)) {
        return true;
    }

    const permissions = claims.customClaims?.permissions;
    if (Array.isArray(permissions)) {
        return permissions.includes(action);
    }

    return false;
}

export function canEditRateProgram(claims: Claims, isValidation: boolean): boolean {
    return canPerformAction(claims, isValidation ? 'edit-validations' : 'edit-rates');
}

export function getFacilityLandingPath(claims: Claims, facilityId: number | string): string {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return `/facility/${facilityId}/overview`;
    }

    if (isAdminRole(claims.roles)) {
        return `/facility/${facilityId}/overview`;
    }

    const identities = getRoleIdentities(claims.roles);

    for (const identity of identities) {
        const segment = ROLE_FACILITY_LANDING[identity];
        if (segment) {
            return `/facility/${facilityId}/${segment}`;
        }
    }

    return `/facility/${facilityId}/overview`;
}

export function getDefaultRedirectPath(claims: Claims): string {
    if (!claims || !claims.roles || claims.roles.length === 0) {
        return '/';
    }

    if (claims.roles.includes(RoleList.SuperAdmin.name)) {
        return '/admin/operators';
    } else if (claims.roles.includes(RoleList.OperatorAdmin.name)) {
        return '/operator/facilities';
    } else if (claims.roles.includes(RoleList.FacilityUser.name)) {
        return '/operator/facilities';
    }

    return '/';
}
